import React, { memo, useState } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, Image, Button, message, Typography } from 'antd';
import { PictureOutlined, PlayCircleFilled, LoadingOutlined, ScanOutlined, DeleteOutlined, ExpandAltOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';
import api from '../../../api/client';
import { useParams } from 'react-router-dom';
import { useGeneration } from '../../../context/GenerationContext';

const { Text } = Typography;

const MarigoldNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { getNodes, getEdges, setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { trackGeneration } = useGeneration();

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    const updateData = (key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        [key]: value
                    }
                };
            }
            return node;
        }));

        if (data.onSave) {
            data.onSave(id, { [key]: value });
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const appEdges = getEdges();
            const appNodes = getNodes();

            // Get Source Image (Left)
            const imageEdge = appEdges.find(e => e.target === id && e.targetHandle === 'left-input');
            let sourceImageUrl = null;
            if (imageEdge) {
                const sourceNode = appNodes.find(n => n.id === imageEdge.source);
                if (sourceNode?.data?.src) {
                    sourceImageUrl = sourceNode.data.src;
                }
            }

            if (!sourceImageUrl) {
                message.warning('Please connect an Image Node to the input handle.');
                setGenerating(false);
                return;
            }

            // Call API
            const response = await api.post('/fal/marigold-depth', {
                image_url: sourceImageUrl
            });

            const { request_id } = response.data;
            const modelId = 'fal-ai/imageutils/marigold-depth';

            trackGeneration(request_id, modelId, {
                prompt: 'Marigold Depth Map',
                type: 'Depth Map',
                canvas_id: canvasId,
                node_id: id,
                promptProp: 'Depth Estimation'
            }, {
                onComplete: async (result) => {
                    if (result && result.image && result.image.url) {
                        updateData('src', result.image.url);
                        message.success('Depth map generated!');
                    } else {
                        message.error('Failed to generate depth map.');
                        console.error('Unexpected result:', result);
                    }
                    setGenerating(false);
                },
                onFail: (err) => {
                    console.error("Depth estimation failed via queue:", err);
                    setGenerating(false);
                }
            });

        } catch (error) {
            console.error('Marigold error:', error);
            message.error('Generation failed: ' + (error.response?.data?.error || error.message));
            setGenerating(false);
        }
    };

    const clearImage = (e) => {
        e.stopPropagation();
        updateData('src', null);
    };

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%', padding: 20 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={300}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Marigold Depth'}
                iconType={data.iconType || 'scan'}
                icon={<ScanOutlined />}
                color={data.nodeColor || '#ff0055'} // Neon pinkish to match depth feeling or reuse qwen
                onTitleChange={onTitleChange}
            />

            {/* Input Handle */}
            <Handle
                type="target"
                position={Position.Left}
                id="left-input"
                style={{
                    ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible)
                }}
            />

            {/* Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                style={getHandleStyle(TYPE_COLORS.image, 'right', handlesVisible)}
            />

            {/* Generate Button */}
            <Button
                type="primary"
                shape="circle"
                icon={generating ? <LoadingOutlined style={{ fontSize: 64 }} /> : <PlayCircleFilled style={{ fontSize: 64 }} />}
                onClick={handleGenerate}
                style={{
                    position: 'absolute',
                    right: -160,
                    bottom: 0,
                    width: 130,
                    height: 130,
                    zIndex: 100,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                    border: '10px solid #161b22',
                    background: '#ff0055',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column', background: '#161b22' }}
                style={{
                    width: '100%',
                    height: '100%',
                    border: generating ? '1px solid #ff0055' : '1px solid #30363d',
                    boxShadow: generating
                        ? '0 0 15px #ff0055, 0 0 30px rgba(255, 0, 85, 0.4)'
                        : '0 4px 12px rgba(0,0,0,0.5)',
                    display: 'flex', flexDirection: 'column',
                    borderRadius: 8,
                    overflow: 'hidden',
                    animation: generating ? 'pulse-glow 2s infinite' : 'none'
                }}
            >
                <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                    {generating && <div className="shimmer-overlay" />}
                    {data.src ? (
                        <div
                            style={{ position: 'relative', width: '100%', height: '100%' }}
                            onMouseEnter={(e) => {
                                const overlay = e.currentTarget.querySelector('.preview-overlay');
                                if (overlay) overlay.style.opacity = 1;
                            }}
                            onMouseLeave={(e) => {
                                const overlay = e.currentTarget.querySelector('.preview-overlay');
                                if (overlay) overlay.style.opacity = 0;
                            }}
                        >
                            <Image
                                src={data.src}
                                alt="depth-map"
                                style={{ width: '100%', height: '100%', objectFit: 'fill' }}
                                preview={{ visible: previewVisible, onVisibleChange: (vis) => setPreviewVisible(vis) }}
                            />

                            <div
                                className="nodrag delete-btn"
                                onClick={clearImage}
                                style={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 5,
                                    background: 'rgba(0,0,0,0.6)',
                                    borderRadius: '50%',
                                    width: 24,
                                    height: 24,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    zIndex: 10
                                }}
                            >
                                <DeleteOutlined style={{ color: '#fff', fontSize: 12 }} />
                            </div>

                            <div
                                className="preview-overlay nodrag"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewVisible(true);
                                }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    background: 'rgba(0,0,0,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    cursor: 'pointer'
                                }}
                            >
                                <ExpandAltOutlined style={{ fontSize: 24, color: '#fff' }} />
                            </div>
                            <style>{`
                                .preview-overlay:hover ~ .delete-btn,
                                .delete-btn:hover {
                                    opacity: 1 !important;
                                }
                            `}</style>
                        </div>
                    ) : (
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#8b949e',
                            padding: 20,
                            textAlign: 'center'
                        }}>
                            <ScanOutlined style={{ fontSize: 48, marginBottom: 16, color: '#ff0055' }} />
                            <Text style={{ color: '#8b949e', fontSize: 14 }}>Connect Image</Text>
                            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
                                Input: Image<br />
                                Output: Depth Map
                            </div>
                        </div>
                    )}
                </div>
            </Card>
            <style>{`
                @keyframes pulse-glow {
                    0% {
                        box-shadow: 0 0 15px #ff0055, 0 0 30px rgba(255, 0, 85, 0.4);
                        border-color: #ff0055;
                    }
                    50% {
                        box-shadow: 0 0 25px #ff5588, 0 0 50px rgba(255, 0, 85, 0.6);
                        border-color: #ff5588;
                    }
                    100% {
                        box-shadow: 0 0 15px #ff0055, 0 0 30px rgba(255, 0, 85, 0.4);
                        border-color: #ff0055;
                    }
                }
            `}</style>
        </div>
    );
};

export default memo(MarigoldNode);
